import React, { useEffect } from 'react'
import { batch, useSignal } from "@preact/signals-react"

import { ACRISS_GROUP_BY } from "@constants"
import { acrissGroupBy, acrissGroups, directSiteBrokerProvider, disableButton, mainProvider, mainSite, offices, officesPickoff, reports, searchID, sites } from "@signals"
import AnalyzerCommonFilters from "Pages/AnalyzersV2/Filters"
import { useAnalyzerOneWayFilters } from "Pages/AnalyzersV2/OneWay/@hooks"
import { oneWaySchema } from "Pages/AnalyzersV2/OneWay/schema"


export default function OneWayFilters() {
	const { onSubmit, isValid, errorRegistry } = useAnalyzerOneWayFilters()

	const isBrokerMode = !!directSiteBrokerProvider.value
	const hasExecution = (!!offices.value && !!reports.value) || !!searchID.value
	// what the third level of the header groups by; only that selector is shown, and it is
	// mandatory, because the axis cannot be built out of "everything"
	const groupBy = acrissGroupBy.value

	// One automatic mode switch per context. Both families can come back empty, and without
	// the guard they would bounce the toggle against each other forever; it also keeps a
	// manual return to an empty mode from being undone instantly. Same as Position.
	const autoSwitchDone = useSignal(false)

	// The group by dimension and the selector it drives open the filter row, in that order.
	// CommonFilters renders every slot in a fixed sequence that would otherwise put the acriss
	// selector to the left of the toggle that chose it, so the pair is pulled to the front with
	// flex order instead of reshuffling a layout shared by every analyzer.
	const groupByOrder = { sx: { order: -2 } }
	const groupByTargetOrder = { sx: { order: -1 } }

	// The reference survives a mode switch. `DirectSiteBrokerProviderToggleButton` wipes both
	// `mainSite` and `mainProvider` inside its own batch, and the selector then reloads against
	// the other family, so by the time anything can react the value is already gone. Each mode
	// has its own reference — the web in Direct Sites, the brand in Broker — so both are kept
	// here and put back as soon as the new list turns out to have them; if one never shows up
	// the field stays empty, which is the honest read.
	const keptMainProvider = useSignal('')
	const keptMainSite = useSignal('')
	const restorePending = useSignal(false)

	useEffect(() => {
		if (mainProvider.value) keptMainProvider.value = mainProvider.value
	}, [mainProvider.value])

	useEffect(() => {
		if (mainSite.value) keptMainSite.value = mainSite.value
	}, [mainSite.value])

	// The autocomplete falls back to `selected: props.value`, and right after a mode switch
	// there is no value left to fall back to. While a restore is pending the remembered
	// reference travels from the filters, so the backend can mark it.
	const restoreReference = (options, kept, reference) => {
		if (!restorePending.value) return

		// the option's own value, not the remembered one: the autocomplete matches its value
		// against the options with `===`, so the type has to be theirs
		const known = (options || []).find(x => String(x.value) === String(kept))

		if (!known) return

		batch(() => {
			reference.value = known.value
			restorePending.value = false
		})
	}

	// A new execution has its own branches and webs, so both selectors start over and let
	// their ajax pick the first option again.
	useEffect(() => {
		batch(() => {
			officesPickoff.value = null
			sites.value = []
			// the reference belonged to the previous execution, which had its own webs and
			// providers; the acriss groups are the axis of the previous one too
			mainProvider.value = ''
			mainSite.value = ''
			keptMainProvider.value = ''
			keptMainSite.value = ''
			restorePending.value = false
			acrissGroups.value = []
			autoSwitchDone.value = false
		})
	}, [reports.value, searchID.value, offices.value])

	// Switching mode swaps the site selector between single and multiple, and the two
	// families of sites (direct vs broker) do not overlap, so the previous selection is
	// dropped — as the empty value of the new shape.
	useEffect(() => {
		batch(() => {
			sites.value = isBrokerMode ? null : []
			// armed for the reloads this switch is about to fire; disarmed by the first list
			// that brings the reference of the mode being entered back
			restorePending.value = !!(isBrokerMode ? keptMainProvider.value : keptMainSite.value)
		})
	}, [directSiteBrokerProvider.value])

	// `directSiteBrokerProvider` is shared with the Brokers and Position analyzers, so the
	// signal's declared default is restored on the way out.
	useEffect(() => () => {
		directSiteBrokerProvider.value = false
	}, [])

	return <AnalyzerCommonFilters
		// every execution option travels scoped to the family the toggle is on
		getDirectSites={!isBrokerMode}
		components={{
			// the reference is a row of the table, so it follows the axis: the web in Direct
			// Sites, the brand in Broker
			mainSite: !isBrokerMode && hasExecution,
			mainProvider: isBrokerMode && hasExecution,
			// in Broker mode both selectors narrow the rows themselves; in Direct Sites they
			// narrow what each web row aggregates. Tiers has no payload key of its own: it
			// writes into `providers`
			providers: hasExecution,
			tiers: hasExecution,
			priceKpiToggle: false,
			webProviderToggle: false,
			// Direct Sites / Broker, exactly as in the Position analyzer
			directSiteBrokerProviderToggle: hasExecution,
			// Branch is a first class field of this analyzer, shown once an execution is chosen
			dropoffOffices: hasExecution,
			acrissGroupBy: hasExecution,
			acrissGroups: hasExecution && groupBy === ACRISS_GROUP_BY.ACRISS_GROUP,
			analyzerAcriss: hasExecution && groupBy === ACRISS_GROUP_BY.ACRISS,
			categories: hasExecution && groupBy === ACRISS_GROUP_BY.CATEGORY,
			lengthRental: hasExecution,
		}}
		compProps={{
			showFeatures: { watchAcriss: false },
			directSiteBrokerProviderToggle: { falseText: 'Direct Sites', trueText: 'Broker' },
			dropoffOffices: {
				grid: { xs: 2 },
				label: 'Branch',
				// the rows are a dimension of the execution, not the branch, so the whole table
				// hangs from one dropoff office
				multiple: false,
				disableClearable: true,
				value: Array.isArray(officesPickoff.value) ? null : officesPickoff.value ?? null,
				// returning at the pickup office is the round trip baseline, not a branch to
				// analyse; the id comes from the url as a string, so both shapes are excluded.
				// Guarded: without a pickup office `Number(null)` is 0 and would hide the
				// option whose value is 0
				excludeOptions: offices.value != null && offices.value !== ''
					? [offices.value, Number(offices.value)]
					: [],
				onChange: e => { officesPickoff.value = e.target.value ?? null },
				onFinishAjax: options => {
					const branches = (options || []).filter(x => String(x.value) !== String(offices.value))

					// no branch under this scope: keeping the previous one would submit a
					// dropoff office the current filters do not offer
					if (!branches.length) {
						officesPickoff.value = null
						return
					}

					// the branch is mandatory, so the first one is picked for the user and only
					// a value this execution actually offers is kept
					const current = officesPickoff.value
					const isKnown = !Array.isArray(current) && branches.some(x => String(x.value) === String(current))

					if (!isKnown) officesPickoff.value = branches[0].value
				},
			},
			dates: { grid: { xs: 2 }, multiple: true, autoSelect: false, disableClearable: false },
			// optional: without a reference the table just has no gaps
			mainProvider: {
				grid: { xs: 2 },
				filters: { selected: restorePending.value ? keptMainProvider.value : '' },
				onFinishAjax: options => restoreReference(options, keptMainProvider.value, mainProvider),
			},
			mainSite: {
				grid: { xs: 2 },
				// the rows are the direct webs of the execution, so the reference is one of
				// them; the broker webs live on the other side of the toggle
				filters: { getDirectSites: true, selected: restorePending.value ? keptMainSite.value : '' },
				onFinishAjax: options => restoreReference(options, keptMainSite.value, mainSite),
			},
			// onFinishAjax overrides the internal handler in LengthOfRentalAutocomplete
			// ({...props} is spread after it), preventing the unconditional selected-option
			// auto-pick that ignores autoSelect
			lengthRental: { multiple: true, disableClearable: false, onFinishAjax: () => { disableButton.value = false } },
			sites: {
				grid: { xs: 2 },
				multiple: !isBrokerMode,
				// The reset effect only runs after this render, so on the render that follows a
				// mode switch the signal still holds the previous shape. MUI throws if a
				// multiple Autocomplete gets a non-array, so the value is coerced to what the
				// selector expects right now.
				value: isBrokerMode
					? (Array.isArray(sites.value) ? null : sites.value ?? null)
					: (Array.isArray(sites.value) ? sites.value : []),
				filters: { getDirectSites: !isBrokerMode },
				onFinishAjax: options => {
					if (options?.length) {
						// Broker mode compares providers inside one web, so the site is picked
						// first and every dependent selector is then scoped by it. Direct Sites
						// narrows an optional set — an empty selection means every direct site,
						// which the backend fills in.
						if (isBrokerMode) {
							sites.value = options[0].value
						}

						return
					}

					// This execution has no webs on this side. The analyzer still works against
					// the other family, so it switches over instead of leaving a dead selector
					// blocking the whole page.
					batch(() => {
						sites.value = isBrokerMode ? null : []
						if (!autoSwitchDone.value) {
							autoSwitchDone.value = true
							directSiteBrokerProvider.value = !isBrokerMode
						}
					})
				},
			},
			acrissGroupBy: { grid: { xs: 1.5, ...groupByOrder } },
			acrissGroups: { grid: { xs: 2, ...groupByTargetOrder } },
			analyzerAcriss: { grid: { xs: 2, ...groupByTargetOrder } },
			categories: { grid: { xs: 2, ...groupByTargetOrder } },
			currency: { grid: { xs: 1.5 } },
		}}
		onSubmit={onSubmit}
		schema={oneWaySchema}
		isValid={isValid}
		errors={errorRegistry}
	/>
}
